// client/src/pages/sections/groups/MigrateMembersModal.js
// Follow-up modal opened by SplitMemberButton after a successful
// split_to_self on a CHILD (not the cohort parent). Lets the partner
// optionally pull other members of the original SG into the newly-
// created solo SG, instead of leaving everyone behind under the old
// anchor.
//
// Props:
//   newSgAnchor       — the just-split client (anchor of the new SG)
//   candidates        — [{ clientCode, clientName, entityType }] from
//                       split_to_self's `eligibleForMigration` array
//   onClose           — close + no-op
//   onResolved        — called after a successful bulk move (refetch)
//   testMode, testSessionId

import { useEffect, useMemo, useState } from 'react';
import { useOptimistic } from './optimisticContext';
import { emitToast, dismissToast, errorText } from './toast';
import { postResolveAction } from './resolveActionQueue';
import { kindOf, requiredConfirmationsForJoin, MIXED_CONFIRM_LABEL } from './resolveVerbConfig';
import ClientChip from './ClientChip';
import { useSubmitGuard } from './useSubmitGuard';

export default function MigrateMembersModal({
  newSgAnchor, anchorEntityType, candidates, onClose, onResolved, testMode, testSessionId,
}) {
  const [selected,   setSelected]   = useState(() => new Set());
  const [submitting] = useState(false);   // optimistic flow closes the modal; no live submitting toggle
  const guard = useSubmitGuard();         // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [error,      setError]      = useState(null);
  const [confirmChecked, setConfirmChecked] = useState({});   // token -> true
  const { dismissByClientCode, restoreIds, markPending, clearPending } = useOptimistic();

  // Mixed-type gate: judged on who is joining. The new group is just the anchor; everyone checked joins it, so
  // a Business / Estate/Trust joining an Individual asks SMLLC and an Individual joining an Individual asks
  // dependent. The server enforces this authoritatively; this just surfaces the toggles up front.
  const requiredConfirm = useMemo(() => {
    const movers = candidates.filter(c => selected.has(c.clientCode)).map(c => kindOf(c.entityType));
    return requiredConfirmationsForJoin([kindOf(anchorEntityType)], movers);
  }, [selected, candidates, anchorEntityType]);
  // Drop a stale confirmation if the selection no longer needs it.
  useEffect(() => {
    setConfirmChecked((prev) => {
      const next = {};
      for (const t of requiredConfirm) if (prev[t]) next[t] = true;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [requiredConfirm]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  function toggle(code) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(candidates.map(c => c.clientCode)));
  }
  function clearAll() {
    setSelected(new Set());
  }

  async function submit() {
    if (selected.size === 0) {
      // Treat "submit nothing" as "skip" so the partner can close the
      // follow-up without clicking Cancel.
      onClose();
      return;
    }
    if (requiredConfirm.some((t) => !confirmChecked[t])) {
      setError(requiredConfirm.length > 1
        ? 'Please check every confirmation before moving these clients.'
        : 'Please check the confirmation before moving these clients.');
      return;
    }
    const codes = [...selected];
    // The new SG anchor's own issues should also stay hidden during
    // the move (the cohort it gains may reshape its rule output).
    const pendingForAction = [newSgAnchor, ...codes];

    // Optimistic dismiss for each moved client (any issues referencing
    // them disappear immediately; the bulk move triggers a server-side
    // revalidate that will regenerate the correct set). markPending
    // keeps them hidden for the full lifecycle, id-independent.
    markPending(pendingForAction);
    const hidden = [];
    for (const c of codes) {
      hidden.push(...dismissByClientCode(c));
    }
    // Optimistic: close the dialog now; the move runs in the background and a
    // failure restores the hidden rows and toasts the server's error.
    onClose();
    const progressToastId = emitToast({
      kind: 'info',
      message: 'Calculating changes based on new groups…',
      durationMs: 0,
    });
    try {
      const data = await postResolveAction({
        verb:       'move_clients_to_sg',
        clientCode: newSgAnchor,                  // dispatcher requires it
        payload:    {
          targetSg: newSgAnchor,
          clientCodes: codes,
          ...(requiredConfirm.length ? { mixedConfirmations: requiredConfirm } : {}),
        },
        testMode:   Boolean(testMode),
        sessionId:  testMode ? testSessionId : null,
      });
      if (onResolved) await onResolved(data);
    } catch (e) {
      restoreIds(hidden);
      emitToast({
        kind: 'error',
        message: `Couldn't migrate members: ${errorText(e)}. Restored.`,
      });
    } finally {
      clearPending(pendingForAction);
      dismissToast(progressToastId);
    }
  }

  return (
    <div
      className="cg-resolve-overlay"
      onClick={() => !submitting && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-resolve-header">
          <h3>Bring others along?</h3>
          <button
            type="button"
            className="cg-resolve-close"
            disabled={submitting}
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </div>
        <div className="cg-resolve-summary">
          <div className="cg-resolve-summary-row">
            <ClientChip code={newSgAnchor} />
            <span className="cg-resolve-summary-name">
              is now its own Signer Group
            </span>
          </div>
        </div>

        <div className="cg-resolve-confirm">
          <p className="cg-resolve-confirm-body">
            The following clients were in <strong>{newSgAnchor}</strong>'s old
            Signer Group. Check anyone who should move into the new group as
            well. Anyone you leave unchecked stays where they are.
          </p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button
              type="button"
              className="cg-btn cg-btn--secondary"
              disabled={submitting || selected.size === candidates.length}
              onClick={selectAll}
            >Select all</button>
            <button
              type="button"
              className="cg-btn cg-btn--secondary"
              disabled={submitting || selected.size === 0}
              onClick={clearAll}
            >Clear</button>
          </div>

          <fieldset className="cg-resolve-field" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {candidates.map((c) => {
              const checked = selected.has(c.clientCode);
              return (
                <label
                  key={c.clientCode}
                  className="cg-resolve-radio-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={submitting}
                    onChange={() => toggle(c.clientCode)}
                  />
                  <span>
                    <ClientChip code={c.clientCode} name={c.clientName} />
                    {c.entityType && (
                      <span className="cg-cell-hint" style={{ marginLeft: 8 }}>
                        ({c.entityType})
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </fieldset>

          {requiredConfirm.map((token) => (
            <label
              key={token}
              className="cg-resolve-radio-row"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: '8px 10px', background: '#fff8e6', border: '1px solid #f0d69a', borderRadius: 6 }}
            >
              <input
                type="checkbox"
                checked={Boolean(confirmChecked[token])}
                disabled={submitting}
                onChange={(e) => setConfirmChecked((prev) => ({ ...prev, [token]: e.target.checked }))}
                style={{ marginTop: 2 }}
              />
              <span>{MIXED_CONFIRM_LABEL[token]}</span>
            </label>
          ))}

          {error && (
            <div className="cg-resolve-error" role="alert" style={{ marginTop: 8 }}>
              <strong>Couldn't migrate:</strong> {error}
            </div>
          )}

          <div className="cg-resolve-actions">
            <button
              type="button"
              className="cg-btn cg-btn--secondary"
              disabled={submitting}
              onClick={onClose}
            >
              {selected.size === 0 ? 'Skip' : 'Cancel'}
            </button>
            <button
              type="button"
              className="cg-btn cg-btn--primary"
              disabled={submitting || selected.size === 0 || requiredConfirm.some((t) => !confirmChecked[t])}
              onClick={() => guard(submit)}
            >
              {submitting
                ? 'Working…'
                : selected.size === 0
                  ? 'Pick at least one'
                  : requiredConfirm.some((t) => !confirmChecked[t])
                    ? 'Confirm to continue'
                    : `Move ${selected.size} into ${newSgAnchor}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
